// @ts-check

import { createEnv } from "@t3-oss/env-nextjs";
import React from "react";

const { experimental_taintUniqueValue: taintUniqueValue } = React;

import { z } from "zod/v4";

export const env = createEnv({
	server: {
		ROOT_STRIPE_CONNECT_CLIENT_ID_PRODUCTION: z.string().startsWith("ca_"),
		ROOT_STRIPE_SECRET_KEY_PRODUCTION: z.string().startsWith("sk_live_"),
		ROOT_STRIPE_YEARLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_MONTHLY_PRICE_ID: z.string().startsWith("price_"),

		ROOT_STRIPE_FREE_MONTHLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_FREE_YEARLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_STARTER_MONTHLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_STARTER_YEARLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_GROWTH_MONTHLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_GROWTH_YEARLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_PRO_MONTHLY_PRICE_ID: z.string().startsWith("price_"),
		ROOT_STRIPE_PRO_YEARLY_PRICE_ID: z.string().startsWith("price_"),

		ROOT_STRIPE_CONNECT_CLIENT_ID_TEST: z.string().startsWith("ca_"),
		ROOT_STRIPE_SECRET_KEY_TEST: z.string().startsWith("sk_test_"),

		DATABASE_URL: z.string().url().startsWith("postgresql://"),

		PROJECT_ID_VERCEL: z.string(),
		TEAM_ID_VERCEL: z.string(),
		AUTH_BEARER_TOKEN_VERCEL: z.string(),

		BLOB_READ_WRITE_TOKEN: z.string(),
		BLOB_HOST: z.string(),

		STRIPE_ROOT_WEBHOOK_SECRET: z.string().optional(),
		STRIPE_WEBHOOK_SECRET: z.string().optional(),

		RESEND_API_KEY: z.string(),

		TRIEVE_API_KEY: z.string(),
		TRIEVE_DATASET_ID: z.string(),

		FURGONETKA_CLIENT_ID_TEST: z.string(),
		FURGONETKA_CLIENT_SECRET_TEST: z.string(),
		FURGONETKA_CLIENT_ID_PRODUCTION: z.string(),
		FURGONETKA_CLIENT_SECRET_PRODUCTION: z.string(),

		// Vercel System Environment Variables
		VERCEL_ENV: z.enum(["production", "preview", "development"]).default("development"),
		VERCEL_REGION: z.string().optional(),
		VERCEL_DEPLOYMENT_ID: z.string().optional(),
		VERCEL_GIT_COMMIT_SHA: z.string().optional(),

		BIR_API_KEY: z.string().optional(),

		OPENEXCHANGERATES_APP_ID: z.string().optional(),

		BETTER_AUTH_SECRET: z.string().min(32),
		GOOGLE_CLIENT_ID: z.string(),
		GOOGLE_CLIENT_SECRET: z.string(),

		YNS_AI_API_KEY: z.string(),
	},
	client: {
		NEXT_PUBLIC_ROOT_URL: z.string().url(),

		NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_TEST: z.string().startsWith("pk_test_"),
		NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_PRODUCTION: z.string().startsWith("pk_live_"),

		NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
		NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),

		NEXT_PUBLIC_LOG_LEVEL: z.enum(["DEBUG", "LOG", "WARN", "ERROR"]).optional().default("DEBUG"),

		NEXT_PUBLIC_VERCEL_ENV: z.enum(["production", "preview", "development"]).default("development"),
		NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
	},
	runtimeEnv: {
		STRIPE_ROOT_WEBHOOK_SECRET: process.env.STRIPE_ROOT_WEBHOOK_SECRET,
		STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,

		NEXT_PUBLIC_ROOT_URL: process.env.NEXT_PUBLIC_ROOT_URL,
		ROOT_STRIPE_CONNECT_CLIENT_ID_PRODUCTION: process.env.ROOT_STRIPE_CONNECT_CLIENT_ID_PRODUCTION,
		ROOT_STRIPE_SECRET_KEY_PRODUCTION: process.env.ROOT_STRIPE_SECRET_KEY_PRODUCTION,
		ROOT_STRIPE_CONNECT_CLIENT_ID_TEST: process.env.ROOT_STRIPE_CONNECT_CLIENT_ID_TEST,
		NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_TEST: process.env.NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_TEST,
		NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_PRODUCTION:
			process.env.NEXT_PUBLIC_ROOT_STRIPE_PUBLISHABLE_KEY_PRODUCTION,
		ROOT_STRIPE_SECRET_KEY_TEST: process.env.ROOT_STRIPE_SECRET_KEY_TEST,
		ROOT_STRIPE_YEARLY_PRICE_ID: process.env.ROOT_STRIPE_YEARLY_PRICE_ID,
		ROOT_STRIPE_MONTHLY_PRICE_ID: process.env.ROOT_STRIPE_MONTHLY_PRICE_ID,

		ROOT_STRIPE_FREE_MONTHLY_PRICE_ID: process.env.ROOT_STRIPE_FREE_MONTHLY_PRICE_ID,
		ROOT_STRIPE_FREE_YEARLY_PRICE_ID: process.env.ROOT_STRIPE_FREE_YEARLY_PRICE_ID,
		ROOT_STRIPE_STARTER_MONTHLY_PRICE_ID: process.env.ROOT_STRIPE_STARTER_MONTHLY_PRICE_ID,
		ROOT_STRIPE_STARTER_YEARLY_PRICE_ID: process.env.ROOT_STRIPE_STARTER_YEARLY_PRICE_ID,
		ROOT_STRIPE_GROWTH_MONTHLY_PRICE_ID: process.env.ROOT_STRIPE_GROWTH_MONTHLY_PRICE_ID,
		ROOT_STRIPE_GROWTH_YEARLY_PRICE_ID: process.env.ROOT_STRIPE_GROWTH_YEARLY_PRICE_ID,
		ROOT_STRIPE_PRO_MONTHLY_PRICE_ID: process.env.ROOT_STRIPE_PRO_MONTHLY_PRICE_ID,
		ROOT_STRIPE_PRO_YEARLY_PRICE_ID: process.env.ROOT_STRIPE_PRO_YEARLY_PRICE_ID,

		DATABASE_URL: process.env.DATABASE_URL,

		NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
		NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,

		PROJECT_ID_VERCEL: process.env.PROJECT_ID_VERCEL,
		TEAM_ID_VERCEL: process.env.TEAM_ID_VERCEL,
		AUTH_BEARER_TOKEN_VERCEL: process.env.AUTH_BEARER_TOKEN_VERCEL,

		BLOB_READ_WRITE_TOKEN: process.env.BLOB_READ_WRITE_TOKEN,
		BLOB_HOST: process.env.BLOB_HOST,

		RESEND_API_KEY: process.env.RESEND_API_KEY,

		TRIEVE_API_KEY: process.env.TRIEVE_API_KEY,
		TRIEVE_DATASET_ID: process.env.TRIEVE_DATASET_ID,

		FURGONETKA_CLIENT_ID_TEST: process.env.FURGONETKA_CLIENT_ID_TEST,
		FURGONETKA_CLIENT_SECRET_TEST: process.env.FURGONETKA_CLIENT_SECRET_TEST,
		FURGONETKA_CLIENT_ID_PRODUCTION: process.env.FURGONETKA_CLIENT_ID_PRODUCTION,
		FURGONETKA_CLIENT_SECRET_PRODUCTION: process.env.FURGONETKA_CLIENT_SECRET_PRODUCTION,

		VERCEL_ENV: process.env.VERCEL_ENV,
		NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
		VERCEL_REGION: process.env.VERCEL_REGION,
		VERCEL_DEPLOYMENT_ID: process.env.VERCEL_DEPLOYMENT_ID,
		VERCEL_GIT_COMMIT_SHA: process.env.VERCEL_GIT_COMMIT_SHA,
		NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,

		BIR_API_KEY: process.env.BIR_API_KEY,

		NEXT_PUBLIC_LOG_LEVEL: process.env.NEXT_PUBLIC_LOG_LEVEL,

		OPENEXCHANGERATES_APP_ID: process.env.OPENEXCHANGERATES_APP_ID,

		BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
		GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
		GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,

		YNS_AI_API_KEY: process.env.YNS_AI_API_KEY,
	},
});

if (taintUniqueValue) {
	void [
		"ROOT_STRIPE_SECRET_KEY_PRODUCTION",
		"ROOT_STRIPE_SECRET_KEY_TEST",
		"DATABASE_URL",
		"AUTH_BEARER_TOKEN_VERCEL",
		"BLOB_READ_WRITE_TOKEN",
		"STRIPE_WEBHOOK_SECRET",
		"STRIPE_ROOT_WEBHOOK_SECRET",
		"RESEND_API_KEY",
		"TRIEVE_API_KEY",
		"FURGONETKA_CLIENT_SECRET_TEST",
		"FURGONETKA_CLIENT_SECRET_PRODUCTION",
		"BIR_API_KEY",
		"OPENEXCHANGERATES_APP_ID",
		"BETTER_AUTH_SECRET",
		"YNS_AI_API_KEY",
		"RESEND_API_KEY",
	].forEach(
		/** @param {any} key */
		(key) => {
			if (key in env) {
				/** @type {keyof typeof env} */
				const _key = key;
				const value = env[_key];
				if (value) {
					taintUniqueValue(`Do not pass ${key} to the client`, globalThis, value);
				}
			} else {
				throw new Error(`A typo or missing environment variable: ${key}`);
			}
		},
	);
}
